import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { PaymentElement, useStripe, useElements, ExpressCheckoutElement, CardNumberElement, CardExpiryElement, CardCvcElement, } from "@stripe/react-stripe-js";
import i18next, { t } from "i18next";
import { container } from "tsyringe";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import * as Sentry from "@sentry/react";
const CheckoutFormInputs = ({ myData, formData, setLoadingCheckout, loadingCheckout, clientSecret, payload, setClientSecret, amountWithoutDecimal, checkSubmit, setCheckSubmit, localCurrencyVisible, ip, localCurrency, onlyCreditCard, setIsValidForm, setPaymentData, generateCheckout, }) => {
    var _a;
    const stripe = useStripe();
    const elements = useElements();
    const bookingServices = container.resolve("IBookingServices");
    const [message, setMessage] = useState("");
    const [cardHolder, setCardHolder] = useState("");
    const [finished, setFinished] = useState(false);
    const [errorCustom, setErrorCustom] = useState(false);
    const [clientSecretPaypal, setClientSecretPaypal] = useState("");
    const [paypalAvailable, setPaypalAvailable] = useState(true);
    const checkoutDisabledMessage = useMemo(() => "Your payment was not successful, please try again.", []);
    const captureGeneralError = (error, context) => {
        let normalizedError;
        if (error instanceof Error) {
            normalizedError = error;
        }
        else if (typeof error === 'object' && error !== null) {
            const e = error;
            normalizedError = new Error(e.message || e.decline_code || JSON.stringify(error));
            normalizedError.name = e.type || e.code || 'PaymentError';
        }
        else {
            normalizedError = new Error(String(error));
        }
        Sentry.captureException(normalizedError, {
            tags: { component: "CheckoutFormInputs", context },
            extra: { originalError: error },
        });
    };
    useEffect(() => {
        const paymentElement = elements === null || elements === void 0 ? void 0 : elements.getElement("payment");
        // Attach the loaderror event handler to PaymentElement
        paymentElement === null || paymentElement === void 0 ? void 0 : paymentElement.on("loaderror", (error) => {
            console.error("Load error occurred in PaymentElement:", error);
            captureGeneralError(error, "payment-element-loaderror");
            setMessage("Failed to load the payment method. Please try again.");
        });
        if (!stripe) {
            return;
        }
        const clientSecret = new URLSearchParams(window.location.search).get("payment_intent_client_secret");
        if (!clientSecret) {
            return;
        }
        stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
            switch (paymentIntent === null || paymentIntent === void 0 ? void 0 : paymentIntent.status) {
                case "succeeded":
                    setMessage("Payment succeeded!");
                    break;
                case "processing":
                    setMessage("Your payment is processing.");
                    break;
                case "requires_payment_method":
                    setMessage("Your payment was not successful, please try again.");
                    break;
                default:
                    setMessage("Something went wrong.");
                    break;
            }
        });
    }, [stripe]);
    const latestIntentKeyRef = useRef();
    useEffect(() => {
        if (!payload || !generateCheckout) {
            return;
        }
        const intentKey = JSON.stringify({
            idSession: payload.idSession,
            checkoutId: payload.checkoutId,
            amount: payload.amount,
            currency: payload.currency,
            name: payload.name,
            email: payload.email,
            paymentType: payload.paymentType,
            offerId: payload.offerId,
            isActiveLocalCurrency: payload.isActiveLocalCurrency,
            split: false,
            paymentMethodTypes: ["card"],
            automaticPaymentMethods: { enabled: false },
            babySeatSelection: payload.babySeatSelection,
        });
        if (latestIntentKeyRef.current === intentKey) {
            return;
        }
        latestIntentKeyRef.current = intentKey;
        const action = bookingServices.getCheckoutIntent;
        // Diagnostic: verify babySeatSelection is present in checkout payload
        console.log("CHECKOUT PAYLOAD babySeatSelection:", payload.babySeatSelection);
        action(payload, null, (data) => {
            setClientSecret(data.clientCode);
            setFinished(true);
        });
    }, [bookingServices, generateCheckout, payload, setClientSecret]);
    const createOrder = () => {
        return new Promise((resolve, reject) => {
            let isResolved = false; // Flag to track resolution
            bookingServices.getCheckoutIntentPaypal(payload, null, async (data) => {
                if (isResolved)
                    return; // Prevent resolving after rejection
                //clearTimeout(timeout);
                sessionStorage.setItem("checkout_intent_id_paypal", data.checkoutId);
                const dataPaymentMethod = {
                    value: 'paypal',
                    id: data.checkoutId,
                    // Set expiration time to 1 day
                    expiration: new Date().getTime() + 24 * 60 * 60 * 1000,
                };
                localStorage.setItem("payment_method", JSON.stringify(dataPaymentMethod));
                setClientSecretPaypal(data.clientCode);
                setFinished(true);
                try {
                    // Send change status for start of payment
                    await bookingServices.updateOfferProcessingPayment({
                        offerId: myData.id,
                        idSession: payload.idSession,
                        status: 2,
                    }, null);
                }
                catch (updateError) {
                    console.error("Error updating payment status:", updateError);
                }
                isResolved = true;
                resolve(data.clientCode);
            });
        });
    };
    useEffect(() => {
        if (checkSubmit) {
            handleSubmit("u");
        }
    }, [checkSubmit]);
    const payloadOnlyDestination = {
        idSession: payload.idSession,
        checkoutId: payload.checkoutId,
        paymentCode: clientSecret,
        isSuccess: true,
        failCode: "",
        failMessage: "",
        paymentMethodType: "card",
        paymentMethodBrand: "stripe",
        paymentMethodData: "1234",
        ...(payload.babySeatSelection ? { babySeatSelection: payload.babySeatSelection } : {}),
    };
    const handleSubmit = async (e) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        if (e.preventDefault) {
            e.preventDefault();
        }
        if (!generateCheckout) {
            setMessage(checkoutDisabledMessage);
            setLoadingCheckout(false);
            setCheckSubmit(false);
            setErrorCustom(true);
            setIsValidForm(false);
            return;
        }
        if (!stripe || !elements) {
            setLoadingCheckout(false);
            setCheckSubmit(false);
            return;
        }
        if (onlyCreditCard) {
            const cardElement = elements.getElement(CardNumberElement);
            if (cardElement) {
                const { error, token } = await stripe.createToken(cardElement);
                if (error) {
                    console.log("[error]", error);
                    // Handle the error, e.g., display the error message to the user
                    setMessage(error.message);
                    setLoadingCheckout(false);
                    setCheckSubmit(false);
                    setErrorCustom(true);
                    setIsValidForm(false);
                    return;
                }
                else {
                    console.log("[token]", token);
                    // The card is valid. You can now use this token to create a charge or attach it to a customer
                    // Typically, you would send this token to your server to complete the payment
                    // await submitPaymentToServer(token.id);
                }
            }
            else {
                console.error("Card element not found");
                setMessage("Card element not found");
                setLoadingCheckout(false);
                setCheckSubmit(false);
                setErrorCustom(true);
                setIsValidForm(false);
                return;
            }
            if (!cardHolder || cardHolder.trim() === "") {
                setMessage(t("card_holder_name") + " " + t("required_field"));
                setLoadingCheckout(false);
                setCheckSubmit(false);
                setErrorCustom(true);
                setIsValidForm(false);
                return;
            }
        }
        setIsValidForm(true);
        setLoadingCheckout(true);
        const { error: submitError } = await elements.submit();
        if (submitError) {
            captureGeneralError(submitError, "elements-submit");
            setMessage(submitError.message || "Payment validation failed. Please try again.");
            setLoadingCheckout(false);
            setCheckSubmit(false);
            setIsValidForm(false);
            return;
        }
        try {
            // Confirm the payment only if the client secret is available
            if (finished && clientSecret != "") {
                if (onlyCreditCard) {
                    const cardElement = elements.getElement(CardNumberElement);
                    const paymentMethodReq = await stripe.createPaymentMethod({
                        type: "card",
                        card: cardElement,
                        billing_details: {
                            name: cardHolder,
                            email: payload.email,
                            address: { country: formData.country },
                        },
                    });
                    if (paymentMethodReq.error) {
                        captureGeneralError(paymentMethodReq.error, "create-payment-method-only-credit-card");
                        setMessage(paymentMethodReq.error.message);
                        setLoadingCheckout(false);
                        setCheckSubmit(false);
                        return;
                    }
                    // ✅ Update payload with real values
                    payloadOnlyDestination.paymentMethodType = (_b = (_a = paymentMethodReq.paymentMethod) === null || _a === void 0 ? void 0 : _a.type) !== null && _b !== void 0 ? _b : "card";
                    payloadOnlyDestination.paymentMethodBrand = (_e = (_d = (_c = paymentMethodReq.paymentMethod) === null || _c === void 0 ? void 0 : _c.card) === null || _d === void 0 ? void 0 : _d.brand) !== null && _e !== void 0 ? _e : "";
                    payloadOnlyDestination.paymentMethodData = (_h = (_g = (_f = paymentMethodReq.paymentMethod) === null || _f === void 0 ? void 0 : _f.card) === null || _g === void 0 ? void 0 : _g.last4) !== null && _h !== void 0 ? _h : "";
                    bookingServices.paymentComplete(payloadOnlyDestination, null, (data) => { });
                    return;
                }
                // Send change status for start of payment
                await bookingServices.updateOfferProcessingPayment({
                    offerId: myData.id,
                    idSession: payload.idSession,
                    status: 2,
                }, null);
                const cardElement = elements.getElement(CardNumberElement);
                if (cardElement && onlyCreditCard) {
                    const paymentMethodReq = await stripe.createPaymentMethod({
                        type: "card",
                        card: cardElement,
                        billing_details: {
                            name: cardHolder,
                            email: payload.email,
                            address: {
                                country: formData.country,
                            },
                        }
                    });
                    if (paymentMethodReq.error) {
                        captureGeneralError(paymentMethodReq.error, "create-payment-method-destination");
                        setMessage(paymentMethodReq.error.message);
                        setLoadingCheckout(false);
                        setCheckSubmit(false);
                        return;
                    }
                    const confirmedCardPayment = await stripe.confirmCardPayment(clientSecret, {
                        payment_method: paymentMethodReq.paymentMethod.id
                    });
                    if (((_j = confirmedCardPayment.paymentIntent) === null || _j === void 0 ? void 0 : _j.status) === "succeeded") {
                        /* ReactGA.initialize('AW-11441363881'); // Use your measurement ID
                         ReactGA.event('conversion', {
                           send_to: 'AW-11441363881/Aj2LCIDSwYEaEKm31c8q',
                           value: amountWithoutDecimal,
                           currency: 'USD',
                           transaction_id: myData.id,
                         });
                         */
                    }
                    else if (confirmedCardPayment.error) {
                        captureGeneralError(confirmedCardPayment.error, "confirm-card-payment");
                        setMessage(confirmedCardPayment.error.message);
                        setLoadingCheckout(false);
                        setCheckSubmit(false);
                    }
                    return;
                }
                const result = await stripe.confirmPayment({
                    clientSecret: clientSecret,
                    elements,
                    redirect: "if_required",
                    confirmParams: {
                        // Make sure to change this to your payment completion page
                        return_url: window.location.href,
                    },
                });
                if (((_k = result.paymentIntent) === null || _k === void 0 ? void 0 : _k.status) === "succeeded") {
                    /* ReactGA.initialize('AW-11441363881'); // Use your measurement ID
                     ReactGA.event('conversion', {
                       send_to: 'AW-11441363881/Aj2LCIDSwYEaEKm31c8q',
                       value: amountWithoutDecimal,
                       currency: 'USD',
                       transaction_id: myData.id,
                     });
                     */
                }
                else if (result.error) {
                    captureGeneralError(result.error, "confirm-payment");
                    setMessage(result.error.message);
                    setLoadingCheckout(false);
                    setCheckSubmit(false);
                }
            }
            else {
                captureGeneralError(new Error("Client secret is not available."), "missing-client-secret");
                console.error("Client secret is not available.");
            }
        }
        catch (error) {
            captureGeneralError(error, "handle-submit");
            console.error("Error during payment confirmation:", error);
            setMessage("An error occurred during payment.");
            setLoadingCheckout(false);
            setCheckSubmit(false);
        }
        finally {
        }
    };
    const paymentElementOptions = {
        layout: {
            type: "tabs",
        },
        paymentMethodOrder: ["card"],
    };
    const paypalOptions = {
        clientId: ((_a = process.env.REACT_APP_PAYPAL_KEY) !== null && _a !== void 0 ? _a : "AbenlSlrGM54M6cQ60KBskWv946khiiAegkwRAdlbCWNxUNGLkPzcvlNU5C0arebk88xnhZBhcneTNUp").toString(),
        currency: "USD",
        intent: "capture",
        disableFunding: "card",
        locale: i18next.language === "en" ? "en_US" : i18next.language == "es" ? "es_ES" : "pt_BR",
    };
    const onApprove = (dataPayment) => {
        sessionStorage.removeItem('pp_checkout_open');
        setLoadingCheckout(true);
        setIsValidForm(true);
        return new Promise((resolve, reject) => {
            bookingServices.captureOrderPaypal({
                idSession: payload.idSession,
                checkoutId: payload.checkoutId,
                OrderId: dataPayment.orderID,
            }, null, () => {
                resolve(dataPayment);
            });
        });
    };
    const onCancel = async (data) => {
        sessionStorage.removeItem('pp_checkout_open');
        await bookingServices.updateOfferProcessingPayment({
            offerId: myData.id,
            idSession: payload.idSession,
            status: 1,
        }, null);
        setLoadingCheckout(false);
        setCheckSubmit(false);
    };
    const handlePaypalError = (error) => {
        setPaypalAvailable(false); // Hide PayPal so Stripe keeps working
        setLoadingCheckout(false);
        setCheckSubmit(false);
        setMessage("");
        captureGeneralError(error, "paypal-buttons");
    };
    const shouldShowPaypal = paypalAvailable && !localCurrency && !onlyCreditCard && generateCheckout;
    const cardStyle = {
        base: {
            fontSize: "16px",
            color: "#000000",
            fontFamily: "Open Sans, sans-serif",
            "::placeholder": {
                color: "#a0a0a0",
                fontSize: "14px",
                fontWeight: "100",
            },
            fontWeight: "100",
        },
        invalid: {
            color: "#EC0101",
        },
    };
    const inputStyle = {
        input: {
            width: "100%",
            padding: "0",
            fontSize: "16px", // For typed text
            borderRadius: "0",
            border: "none",
            //remove the bold text 
            fontWeight: "normal",
        },
        customInputContainer: {
            border: errorCustom ? "1px solid #EC0101" : "1px solid #EAEAEA",
            borderRadius: "0.5rem",
            padding: "0.75rem",
            marginBottom: "7px",
            backgroundColor: "#fff",
        },
        css: `
    .cardholder-input::placeholder {
      font-size: 14px;
      color: #a0a0a0;
    }
    
  `,
    };
    return (_jsxs(_Fragment, { children: [_jsx("style", { children: inputStyle.css }), _jsxs("form", { id: "payment-form", onSubmit: handleSubmit, children: [shouldShowPaypal && (_jsx(Sentry.ErrorBoundary, { fallback: _jsx(_Fragment, {}), onError: (error) => handlePaypalError(error), children: _jsx("div", { className: "paypal-buttons-fluid", children: _jsx(PayPalScriptProvider, { options: paypalOptions, children: _jsx(PayPalButtons, { className: "paypal-buttons-fluid__button", style: { layout: "vertical", shape: "rect", disableMaxWidth: true }, createOrder: createOrder, onApprove: onApprove, onCancel: onCancel, onClick: () => sessionStorage.setItem('pp_checkout_open', '1'), onError: handlePaypalError }) }) }) })), message && !onlyCreditCard && (_jsx("div", { className: "transaction-form__message --error", id: "payment-message", children: message })), !onlyCreditCard && generateCheckout && _jsx(ExpressCheckoutElement, { onConfirm: handleSubmit }), _jsx("div", { className: "transaction__valuepay transaction__valuepay--innercards", children: _jsxs("div", { className: "transaction__block", style: {
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center", // opcional: alinea verticalmente
                                width: "100%",
                            }, children: [_jsxs("span", { className: "valuepay-text strong", children: [t("card-credit-debit"), " "] }), onlyCreditCard && (_jsxs("div", { className: "transaction__valuepay--innercards__icon", style: { display: "flex", gap: "4px", alignItems: "center" }, children: [_jsx("img", { src: "https://cdn.shopify.com/s/files/1/0816/6431/1592/files/Logo-VISA-input.svg?v=1722622792", alt: "Visa", style: { height: "24px", width: "auto" } }), _jsx("img", { src: "https://cdn.shopify.com/s/files/1/0816/6431/1592/files/Logo-MasterCard-input.svg?v=1722622811", alt: "Mastercard", style: { height: "24px", width: "auto" } }), _jsx("img", { src: "https://cdn.shopify.com/s/files/1/0816/6431/1592/files/Logo-diners-input.svg?v=1722876278", alt: "Discover", style: { height: "24px", width: "auto" } })] }))] }) }), onlyCreditCard && (_jsxs(_Fragment, { children: [message && (_jsx("div", { className: "transaction-form__message --error", id: "payment-message", style: { marginBottom: "0.5rem" }, children: message })), _jsx("div", { className: "custom-input", style: inputStyle.customInputContainer, children: _jsx(CardNumberElement, { id: "payment-element", options: { style: cardStyle, placeholder: t("card_number") } }) }), _jsx("div", { className: "custom-input", style: inputStyle.customInputContainer, children: _jsx("input", { id: "cardholder-name", name: "cardholder-name", type: "text", className: "cardholder-input", style: inputStyle.input, placeholder: t("card_holder_name"), value: cardHolder, onChange: (e) => {
                                        setCardHolder(e.target.value);
                                        setErrorCustom(false);
                                    } }) }), _jsx("div", { className: "custom-input", style: inputStyle.customInputContainer, children: _jsx(CardExpiryElement, { id: "payment-element", options: { style: cardStyle, placeholder: t("card_expiry") } }) }), _jsx("div", { className: "custom-input", style: inputStyle.customInputContainer, children: _jsx(CardCvcElement, { id: "payment-element", options: { style: cardStyle, placeholder: t("card_cvc") } }) })] })), !onlyCreditCard && _jsx(PaymentElement, { id: "payment-element", options: paymentElementOptions })] })] }));
};
export default CheckoutFormInputs;
